import axios from 'axios';

function resolveApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8080/api';
    }
  }
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
}

const client = axios.create({
  baseURL: resolveApiBaseUrl(),
});

export const ONBOARDING_DISTRIBUTOR_ID = process.env.REACT_APP_DISTRIBUTOR_ID ? Number(process.env.REACT_APP_DISTRIBUTOR_ID) : undefined;

// Simple onboarding token storage (persisted)
let onboardingToken: string | null = null;
const TOKEN_KEY = 'onboardingToken';

function initTokenFromStorage() {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) onboardingToken = t;
  } catch (_) {
    // ignore storage errors
  }
}
initTokenFromStorage();

export function setOnboardingToken(token: string) {
  onboardingToken = token;
  try { localStorage.setItem(TOKEN_KEY, token); } catch (_) {}
}

export function hasOnboardingToken() {
  return Boolean(onboardingToken);
}

export function clearOnboardingToken() {
  onboardingToken = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}
  try { localStorage.removeItem(APP_ID_KEY); } catch (_) {}
  try { localStorage.removeItem(CONDITIONS_KEY); } catch (_) {}
  try { localStorage.removeItem(PARTNER_CONDITIONS_KEY); } catch (_) {}
}

const APP_ID_KEY = 'onboardingApplicationId';
const CONDITIONS_KEY = 'onboardingConditionsAccepted';
const PARTNER_CONDITIONS_KEY = 'partnerConditionsAccepted';

export function setOnboardingApplicationId(id: number) {
  try { localStorage.setItem(APP_ID_KEY, String(id)); } catch (_) {}
}

export function getOnboardingApplicationId(): number | null {
  try {
    const n = Number(localStorage.getItem(APP_ID_KEY) || '');
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (_) {
    return null;
  }
}

/** Client-only merchant-conditions acceptance until the application is created. */
export function setMerchantConditionsAcceptedLocal(accepted = true) {
  try {
    if (accepted) localStorage.setItem(CONDITIONS_KEY, '1');
    else localStorage.removeItem(CONDITIONS_KEY);
  } catch (_) {}
}

export function getMerchantConditionsAcceptedLocal(): boolean {
  try {
    return localStorage.getItem(CONDITIONS_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function clearMerchantConditionsAcceptedLocal() {
  try { localStorage.removeItem(CONDITIONS_KEY); } catch (_) {}
}

export function setPartnerConditionsAcceptedLocal(accepted = true) {
  try {
    if (accepted) localStorage.setItem(PARTNER_CONDITIONS_KEY, '1');
    else localStorage.removeItem(PARTNER_CONDITIONS_KEY);
  } catch (_) {}
}

export function getPartnerConditionsAcceptedLocal(): boolean {
  try {
    return localStorage.getItem(PARTNER_CONDITIONS_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function clearPartnerConditionsAcceptedLocal() {
  try { localStorage.removeItem(PARTNER_CONDITIONS_KEY); } catch (_) {}
}

export function getOnboardingSubject(): { channel: string; value: string } | null {
  try {
    const token = onboardingToken;
    if (!token) return null;
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (part.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    const sub = String(json.sub || '');
    const idx = sub.indexOf(':');
    if (idx < 1) return null;
    return { channel: sub.slice(0, idx), value: sub.slice(idx + 1) };
  } catch (_) {
    return null;
  }
}

client.interceptors.request.use((config) => {
  if (onboardingToken) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${onboardingToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    if (status === 401 && (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED')) {
      clearOnboardingToken();
    }
    return Promise.reject(err);
  }
);

export default client;
